import { getSchemaForRole } from "./schemas";
import type { MessageValidationError, ValidationResult } from "./types";

const createErrorResult = (message: string): ValidationResult => ({
  success: false,
  parseError: message,
  messageErrors: new Map(),
  data: null,
});

/**
 * Validates a JSON string containing an array of messages.
 *
 * Two-level validation:
 * 1. Parse level: JSON.parse() and array check
 * 2. Structural level: Per-message Zod schema validation
 *
 * @param jsonString - The JSON string to validate
 * @returns ValidationResult with errors and parsed data
 */
export function validateMessagesJson(jsonString: string): ValidationResult {
  // Level 1: Parse JSON
  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonString);
  } catch (e) {
    return createErrorResult(e instanceof Error ? e.message : "Invalid JSON syntax");
  }

  // Must be an array
  if (!Array.isArray(parsed)) {
    return createErrorResult("Expected a JSON array of messages");
  }

  // Level 2: Validate each message structure
  const messageErrors = new Map<string, MessageValidationError[]>();

  for (let i = 0; i < parsed.length; i++) {
    const msg = parsed[i] as Record<string, unknown> | null;

    // Basic object check
    if (!msg || typeof msg !== "object") {
      const msgId = `index-${i}`;
      messageErrors.set(msgId, [
        {
          messageId: msgId,
          path: [],
          message: "Message must be an object",
        },
      ]);
      continue;
    }

    const role = msg.role as string | undefined;
    const msgId = (msg.id as string) ?? `index-${i}`;

    // Get role-specific schema and validate
    const schema = getSchemaForRole(role ?? "");
    const result = schema.safeParse(msg);

    if (!result.success) {
      const errors: MessageValidationError[] = result.error.issues.map(issue => ({
        messageId: msgId,
        path: issue.path.map(String),
        message: issue.message,
      }));
      messageErrors.set(msgId, errors);
    }
  }

  return {
    success: messageErrors.size === 0,
    parseError: null,
    messageErrors,
    data: parsed,
  };
}

/**
 * Formats validation errors for display in a tooltip.
 * Returns a friendly message on the first line, technical details below.
 *
 * @param errors - Array of validation errors for a single message
 * @returns Formatted string for tooltip display
 */
export function formatValidationErrors(errors: MessageValidationError[]): string {
  if (errors.length === 0) {
    return "";
  }

  // Group by unique error messages to avoid repetition
  const uniqueErrors = [...new Map(errors.map(e => [e.message + e.path.join("."), e])).values()];

  // Friendly summary
  const friendlyMessage =
    uniqueErrors.length === 1
      ? "Invalid message structure"
      : `${uniqueErrors.length} validation issues`;

  // Technical details
  const details = uniqueErrors
    .map(e => (e.path.length > 0 ? `${e.path.join(".")}: ${e.message}` : e.message))
    .join("\n");

  return `${friendlyMessage}\n${details}`;
}
